import React, { useEffect } from "react";
import {
  FaStethoscope,
  FaHeartbeat,
  FaUserMd,
  FaNotesMedical,
  FaSyringe,
  FaFileMedical,
  FaBaby,
  FaVenus,
  FaFileInvoice,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import Snowfall from "../components/ui/Snowfall";
import { Helmet } from "react-helmet-async";

export default function MedicalServicesPage() {
  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  return (
    <div id="medical" className="scroll-mt-28 relative">

      {/* ❄ Soft Festive Snow — does not obstruct content */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Snowfall count={45} />
      </div>

      {/* INTRO SECTION */}
      <section
        className="relative z-10 py-20 px-6 bg-white text-gray-700 max-w-4xl mx-auto"
        data-aos="fade-up"
      >
        <Helmet>
          <title>Greenwich Clinic Services</title>
          <meta name="description" content="Family Practice, Walk-in Clinic, Minor Illness Treatment, Chronic Disease Management, Women's Health, Routine Physical Examination, IUD & Contraceptiive Implant Insertion, Vaccines & Immunizations, " />
        </Helmet>
        {/* Heading */}
        <div className="inline-block mb-8 relative">
          <h2 className="text-4xl md:text-5xl font-extrabold text-teal-800 tracking-tight">
            Medical Services
          </h2>
          <div className="absolute w-24 h-1 bg-teal-600 bottom-0 left-0 rounded-full"></div>
        </div>

        {/* Intro Paragraphs */}
        <div className="space-y-7 text-base md:text-lg leading-relaxed mt-4">
          <p className="flex items-start gap-3">
            <FaStethoscope className="text-teal-600 mt-1 text-xl" />
            At Greenwich Medical Clinic, we’re committed to delivering
            high-quality, compassionate care for patients at every stage of life.
          </p>

          <p className="flex items-start gap-3">
            <FaUserMd className="text-teal-600 mt-1 text-xl" />
            From preventive exams to women’s health, chronic disease management,
            urgent concerns, and walk-ins — our team is here to support you.
          </p>

          <ul className="list-disc list-inside space-y-2 pl-6 pt-2 text-gray-800">
            <li>Annual check-ups & physical exams</li>
            <li>Vaccines & immunizations</li>
            <li>Pediatric & pregnancy testing services</li>
            <li>Women’s health screening & Pap tests</li>
            <li>Uninsured services available — including IFHP for refugees</li>
          </ul>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section
        className="relative z-10 bg-gradient-to-br from-teal-300 via-white to-teal-100 py-20 px-6"
        data-aos="fade-up"
      >
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-extrabold text-center text-teal-700 mb-14">
            Walk-In & Appointment-Based Services
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map(({ title, description, icon }) => (
              <div
                key={title}
                className="group bg-teal-600 rounded-2xl shadow-lg p-7 text-white text-center
                transition-all duration-300 hover:scale-[1.04] hover:shadow-2xl
                flex flex-col items-center"
              >
                <div className="mb-4">{icon}</div>

                <h4 className="text-lg md:text-xl font-semibold mb-2">
                  {title}
                </h4>

                <p className="text-sm md:text-base leading-relaxed opacity-90">
                  {description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// Service data in navbar order all inclusive 13 items
const services = [
  {
    title: "Family Practice",
    icon: <FaUserMd className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Comprehensive care for all ages — from children to seniors. We build long-term relationships for your lifelong health journey.",
  },
  {
    title: "Walk-in Clinic",
    icon: <FaNotesMedical className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "No appointment needed. Quick access to care for common illnesses, infections, and minor concerns.",
  },
  {
    title: "IUD & Contraceptive Implant Insertion",
    icon: <FaVenus className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Now offering IUD and contraceptive implant insertion with professional, confidential care.",
  },
  {
    title: "Spanish-Speaking Physician",
    icon: <FaUserMd className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Walk-in or book an appointment with our Spanish-speaking doctor available on-site.",
  },
  {
    title: "Telemedicine",
    icon: <FaStethoscope className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Virtual consultations from the comfort of your home. Get expert advice without traveling.",
  },
  {
    title: "Immunization",
    icon: <FaSyringe className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Protect yourself and your loved ones from serious diseases. We offer a full range of routine childhood and adult vaccines, including travel immunizations for global destinations.",
  },
  {
    title: "Pap Smear / Pap Test",
    icon: <FaFileMedical className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "A crucial screening test for cervical cancer and other abnormalities. Early detection helps in timely treatment and better health outcomes for women.",
  },
  {
    title: "Chronic & Minor Illness Care",
    icon: <FaHeartbeat className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Comprehensive care for chronic conditions such as diabetes, hypertension, and asthma, along with treatment for minor illnesses like colds, flu, and infections. Walk-ins welcome.",
  },
  {
    title: "Routine Physical Exams",
    icon: <FaStethoscope className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Annual check-ups including vital signs, screening tests, and preventive consultations.",
  },
  {
    title: "Women's Health",
    icon: <FaVenus className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Personalized care for women of all ages — from menstruation to menopause.",
  },
  {
    title: "Pregnancy Testing",
    icon: <FaBaby className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Confidential, fast, and accurate tests with supportive follow-up care.",
  },
  {
    title: "Treatment for Minor Injuries",
    icon: <FaHeartbeat className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Prompt care for cuts, burns, sprains, and other non-life-threatening injuries. Walk-in welcome.",
  },
  {
    title: "Medical Notes & Forms",
    icon: <FaNotesMedical className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Sick notes, school forms, or fitness clearance — customized to your need.",
  },
  {
    title: "Health Education",
    icon: <FaUserMd className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Get guidance on disease prevention, nutrition, fitness, and overall well-being.",
  },
  {
    title: "Uninsured Services",
    icon: <FaFileInvoice className="text-5xl text-white group-hover:scale-110 transition-transform duration-300" />,
    description: "Affordable private-pay options for services not covered by public health plans. This includes the IFHP (Interim Federal Health Program) for refugees.",
  },
];
